import { getReportPlatform } from '@/utils/reportPlatform'

/**
 * Flatten the RAW campaign report posts (as returned by the passcode endpoint
 * /api/v1/campaigns-html-report/:id/report) into the flat, snake_case shape the
 * summary tables use.
 *
 * This is a faithful port of `calculatePostData` + the per-type derived-metric
 * block in components/reports/index.vue, kept standalone so the Kobe report and
 * the legacy summary report present identical numbers. The passcode endpoint
 * does NOT run formatRptPost, so this normalisation must happen client-side
 * (the legacy report does the same).
 */

const n = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);
const round2 = (v) => Number((Number.isFinite(v) ? v : 0).toFixed(2));

// mirror utils.formatDate: 'YYYY-MM-DD' -> 'DD/MM/YYYY'
function formatDate(date) {
  if (!date) return '-';
  const parts = String(date).split('-');
  if (parts.length < 3) return date;
  const out = `${parts[2]}/${parts[1]}/${parts[0]}`;
  return out.includes('undefined') ? date : out;
}
// mirror campaignHelper.parseWatchTime (best-effort)
function parseWatchTime(watch) {
  if (watch === null || watch === undefined || watch === '') return 0;
  const s = String(watch);
  return s.includes('s') ? s : n(s);
}

// Port of index.vue calculatePostData(post) — raw post -> flat postData.
function calculatePostData(post, index) {
  if (post.isCalculated) return post;
  const platform = getReportPlatform(post)
  const d = {
    id: post.id,
    campaignID: post.campaignID,
    key: index,
    lineUpIndex: post.lineUpIndex,
    groupID: post.groupID,
    groupName: post.groupName,
    // Prefer a stored profile URL when the report payload provides one. The
    // report renderer falls back to the content platform below when it does
    // not. Platform and handle are supplied by the accepted pairing, rather
    // than inferred from a content type.
    profileUrl: post.profileUrl || post.profile_url || (post.owner || {}).profileUrl || (post.owner || {}).profile_url || (post.owner || {}).profileURL || '',
    profilePlatform: post.profilePlatform || post.profile_platform || (post.influencer_details || {}).platform || post.platform || '',
    profileHandle: post.profileHandle || post.profile_handle || (post.influencer_details || {}).instagramHandle || ''
  };

  if (!['linkedin', 'twitter'].includes(platform) && !post.isStory && !post.isTikTok && !post.isMultipleContents && !post.isYoutube && !post.isXHS && !post.isLemon && !post.isFacebook && !post.isOthers) {
    const ci = post.currentInsight || {};
    const pd = post.post_details || {};
    d.name = (pd.owner || {}).username;
    d.url = pd.link;
    d.impressions = 0;
    d.engagement = ci.engagement || 0;
    d.reach = 0;
    d.likes = ci.like_count || 0;
    d.comments = ci.comment_count || 0;
    d.outreach = ci.outreach || 0;
    d.engagement_rate = post.currentInsight ? Number(ci.engagement_rate) : 1;
    d.median_engagement_rate = post.median_engagement_rate ? Number(post.median_engagement_rate) : 0;
    d.avg_likes = n(post.avg_likes);
    d.avg_comments = n(post.avg_comments);
    d.avg_views = n(post.avg_views);
    d.avg_reels_plays = n(post.avg_reels_plays);
    if (post.postType === 'IGR') { d.type = 'reel'; d.title = 'IG Reel'; } else { d.type = 'post/carousel'; d.title = 'IG Post'; }
    if (post.inputtedInsights) {
      const ii = post.inputtedInsights;
      d.saved = ii.saved; d.shares = ii.shares; d.views = ii.views || 0;
      d.reach = ii.reach || 0; d.impressions = ii.impressions || 0;
      d.plays = ii.plays || 0; d.watches = ii.watches ? parseWatchTime(ii.watches) : 0;
    } else {
      d.saved = post.graphInsight ? post.graphInsight.saved : 0; d.shares = 0; d.views = 0;
    }
  } else if (post.isStory && post.insights) {
    const ins = post.insights; const ow = post.owner || {};
    d.name = ow.username;
    d.outreach = ow.followers || ow.followers_sd;
    d.title = 'IG Story';
    d.url = post.media_url || '';
    d.engagement = n(ins.engagement);
    d.impressions = n(ins.impressions);
    d.story_likes = n(ins.story_likes);
    d.reach = n(ins.reach);
    d.replies = n(ins.replies);
    d.exits = n(ins.exits);
    d.taps_back = n(ins.taps_back);
    d.taps_forward = n(ins.taps_forward);
    d.shares = n(ins.shares);
    d.sticker_taps = n(ins.sticker_taps);
    d.link_clicks = n(ins.link_clicks);
    d.avg_views = n(post.avg_views);
    d.avg_reels_plays = n(post.avg_reels_plays);
    d.type = 'story';
    d.storyTotalInteractions = d.link_clicks + d.replies + d.shares + d.sticker_taps + d.story_likes;
  } else if (post.isTikTok) {
    const ci = post.currentInsight || {}; const pd = post.post_details || {}; const ow = post.owner || {};
    d.name = ow.username;
    d.title = 'TikTok Post';
    d.url = pd.link;
    d.impressions = 0;
    d.engagement = ci.engagement || 0;
    d.reach = 0;
    d.likes = ci.like_count || 0;
    d.comments = ci.comment_count || 0;
    d.shares = ci.shares || 0;
    d.views = ci.views || 0;
    d.outreach = ci.outreach || 0;
    d.engagement_rate = post.currentInsight ? Number(ci.engagement_rate) : 1;
    d.median_engagement_rate = post.median_engagement_rate ? Number(post.median_engagement_rate) : 0;
    d.avg_likes = n(post.avg_likes); d.avg_comments = n(post.avg_comments);
    d.avg_views = n(post.avg_views); d.avg_saves = n(post.avg_saves); d.avg_shares = n(post.avg_shares);
    d.type = 'tiktok';
    if (post.inputtedInsights) {
      const ii = post.inputtedInsights;
      d.saved = ii.saved;
      if (d.shares === 0) d.shares = ii.shares;
      if (d.views === 0) d.views = ii.views || 0;
      d.reach = ii.reach || 0; d.impressions = ii.impressions || 0;
      d.plays = ii.plays || 0; d.watches = ii.watches ? parseWatchTime(ii.watches) : 0;
      d.watchedFullVideo = ii.watchedFullVideo || 0; d.photo_views = ii.photo_views || 0;
    } else {
      d.saved = post.graphInsight ? post.graphInsight.saved : 0;
      d.shares = 0; d.views = 0; d.reach = 0; d.impressions = 0; d.plays = 0; d.watches = 0; d.watchedFullVideo = 0;
    }
  } else if (post.isYoutube) {
    const ci = post.currentInsight || {}; const pd = post.post_details || {}; const ow = post.owner || {};
    d.name = ow.username; d.title = 'Youtube Post'; d.url = pd.link; d.type = 'youtube';
    d.impressions = 0; d.engagement = ci.engagement || 0; d.reach = 0;
    d.likes = ci.like_count || 0; d.comments = ci.comment_count || 0; d.views = ci.views || 0;
    d.outreach = ci.outreach || 0;
    d.engagement_rate = post.currentInsight ? Number(ci.engagement_rate) : 1;
    d.median_engagement_rate = post.median_engagement_rate ? Number(post.median_engagement_rate) : 0;
    d.avg_likes = n(post.avg_likes); d.avg_comments = n(post.avg_comments); d.avg_views = n(post.avg_views);
  } else if (post.isFacebook && post.insights) {
    const ins = post.insights; const ow = post.owner || {};
    d.name = ow.username; d.title = 'Facebook'; d.url = post.media_url || '';
    d.outreach = n(ins.outreach); d.like_count = n(ins.like_count); d.comment_count = n(ins.comment_count);
    d.views = n(ins.views); d.shares = n(ins.shares); d.bookmarks = n(ins.bookmarks); d.type = 'facebook';
    d.likes = d.like_count; d.comments = d.comment_count; d.saved = d.bookmarks;
  } else if (post.isXHS && post.insights) {
    const ins = post.insights; const ow = post.owner || {};
    d.name = ow.fullName || ow.username;
    d.outreach = ow.followers ? Number(ow.followers) : ow.followers_sd;
    d.title = 'XHS'; d.url = post.embed_url || ''; d.xhsLink = post.xhsLink || '';
    d.like_count = n(ins.like_count); d.comment_count = n(ins.comment_count); d.views = n(ins.views);
    d.watches = n(ins.watches); d.shares = n(ins.shares); d.bookmarks = n(ins.bookmarks); d.type = 'xhs';
    d.likes = d.like_count; d.comments = d.comment_count; d.saved = d.bookmarks;
  } else if (post.isLemon && post.insights) {
    const ins = post.insights; const ow = post.owner || {};
    d.name = ow.fullName || ow.username;
    d.outreach = ow.followers ? Number(ow.followers) : ow.followers_sd;
    d.title = 'Lemon8'; d.url = post.embed_url || ''; d.lemonLink = post.lemonLink || '';
    d.like_count = n(ins.like_count); d.comment_count = n(ins.comment_count); d.views = n(ins.views);
    d.watches = n(ins.watches); d.shares = n(ins.shares); d.bookmarks = n(ins.bookmarks); d.type = 'lemon';
    d.likes = d.like_count; d.comments = d.comment_count; d.saved = d.bookmarks;
  } else if (platform === 'twitter' && post.insights) {
    const latestInsight = Array.isArray(post.insights) ? post.insights[post.insights.length - 1] : null;
    const insightData = latestInsight && latestInsight.data ? latestInsight.data : post.insights;
    const ins = Object.assign({}, insightData || {}, post.currentInsight || {}, post.inputtedInsights || {}); const ow = post.owner || {};
    d.name = ow.twitterHandle || ow.username || ow.fullName;
    d.outreach = n(ins.outreach || ow.followers || ow.followers_sd);
    const rawUrl = post.postLink || post.postURL || post.post_details || post.url || post.embed_url || '';
    d.title = 'X (Twitter) Post';
    d.url = typeof rawUrl === 'string' ? rawUrl : (rawUrl.link || rawUrl.url || rawUrl.href || '');
    d.like_count = n(ins.like_count); d.comment_count = n(ins.comment_count); d.impressions = n(ins.impressions != null ? ins.impressions : ins.views); d.views = d.impressions;
    d.shares = n(ins.shares); d.bookmarks = n(ins.saved || ins.bookmarks); d.type = 'twitter';
    d.likes = d.like_count; d.comments = d.comment_count; d.saved = d.bookmarks;
  } else if ((platform === 'linkedin' || post.isOthers) && post.insights) {
    const isLinkedIn = platform === 'linkedin'
    const latestInsight = Array.isArray(post.insights) ? post.insights[post.insights.length - 1] : null;
    const insightData = latestInsight && latestInsight.data ? latestInsight.data : post.insights;
    const ins = Object.assign({}, insightData || {}, post.currentInsight || {}, post.inputtedInsights || {}); const ow = post.owner || {};
    const influencer = post.influencer_details || {};
    d.name = ow.fullName || ow.username;
    d.outreach = n(ins.outreach);
    d.title = isLinkedIn ? 'LinkedIn Post' : 'Others';
    d.media_type = post.media_type ? String(post.media_type).toLowerCase() : null;
    d.url = isLinkedIn ? (post.postLink || post.postURL || '') : (post.embed_url || '');
    d.postLink = post.postLink || post.postURL || '';
    d.like_count = n(ins.like_count); d.comment_count = n(ins.comment_count); d.views = n(ins.views);
    d.impressions = n(ins.impressions); d.reach = n(ins.reach != null ? ins.reach : ins.outreach);
    d.watches = n(ins.watches); d.shares = n(ins.shares); d.bookmarks = n(ins.saved || ins.bookmarks); d.plays = n(ins.plays);
    d.type = isLinkedIn ? 'linkedin' : 'others';
    d.likes = d.like_count; d.comments = d.comment_count; d.saved = d.bookmarks;
    if (isLinkedIn) {
      d.profilePlatform = 'linkedin';
      d.profileUrl = [
        post.linkedinURL, post.linkedinUrl, post.linkedin_profile_url,
        ow.linkedinURL, ow.linkedinUrl, ow.linkedin_profile_url, ow.profileUrl, ow.profile_url,
        influencer.linkedinURL, influencer.linkedinUrl, influencer.linkedin_profile_url, influencer.profileUrl, influencer.profile_url,
        d.profileUrl
      ].find(Boolean) || '';
      d.profileHandle = [
        post.linkedinHandle, post.linkedin_handle,
        ow.linkedinHandle, ow.handle, ow.username,
        influencer.linkedinHandle, influencer.handle,
        d.profileHandle, d.name
      ].find(Boolean) || '';
    }
  } else if (post.isMultipleContents) {
    // CC-uploaded "IG Story" contents; media is an S3 URL on embed_url
    const ow = post.owner || {};
    d.name = ow.username;
    d.outreach = ow.followers || ow.followers_sd;
    d.title = 'IG Story';
    d.url = post.embed_url || '';
    d.type = 'gdrive';
    d.media_type = post.media_type ? String(post.media_type).toLowerCase() : null;
    d.public_id = post.public_id || null;
    d.avg_views = n(post.avg_views);
    d.avg_reels_plays = n(post.avg_reels_plays);
    if (post.insights) {
      const ins = post.insights;
      d.engagement = n(ins.engagement);
      d.impressions = n(ins.impressions);
      d.reach = n(ins.reach);
      d.replies = n(ins.replies);
      d.exits = n(ins.exits);
      d.taps_back = n(ins.taps_back);
      d.taps_forward = n(ins.taps_forward);
      d.shares = n(ins.shares);
      d.sticker_taps = n(ins.sticker_taps);
      d.link_clicks = n(ins.link_clicks);
      d.storyTotalInteractions = d.link_clicks + d.replies + d.shares + d.sticker_taps;
    }
  }

  d.live_date = post.live_date ? formatDate(post.live_date) : '-';
  d.liveDate = d.live_date;
  d.isCalculated = true;
  return d;
}

// Per-type derived metrics — mirror of index.vue's per-array .map() blocks.
function applyDerived(p) {
  const likes = n(p.likes); const comments = n(p.comments); const saved = n(p.saved); const shares = n(p.shares);
  const outreach = n(p.outreach); const views = n(p.views); const reach = n(p.reach);
  if (p.type === 'story' || p.type === 'gdrive') {
    p.interactions = n(p.shares) + n(p.replies) + n(p.link_clicks) + n(p.sticker_taps) + n(p.story_likes);
    p.new_engagement_rate = outreach ? round2((p.interactions / outreach) * 100) : 0;
  } else if (p.type === 'tiktok') {
    p.interactions = likes + comments + saved + shares;
    p.new_engagement_rate = outreach ? round2(((likes + comments + shares) / outreach) * 100) : 0;
    p.interaction_rate_plays = views ? round2(((likes + comments + shares) / views) * 100) : 0;
    p.eng_rate_total_viewers = views && reach ? round2(((likes + comments + saved + shares) / reach) * 100) : 0;
    p.new_engagement_rate_view = outreach ? round2((views / outreach) * 100) : 0;
  } else if (p.type === 'youtube') {
    p.interactions = likes + comments;
    p.new_engagement_rate = outreach ? round2(((likes + comments) / outreach) * 100) : 0;
    p.new_engagement_rate_view = outreach ? round2((views / outreach) * 100) : 0;
  } else if (p.type === 'facebook' || p.type === 'xhs' || p.type === 'lemon' || p.type === 'twitter') {
    p.interactions = n(p.like_count) + n(p.comment_count) + n(p.bookmarks) + shares;
  } else {
    // post/carousel + reel
    p.interactions = likes + comments + saved + shares;
    p.new_engagement_rate = outreach ? round2(((likes + comments + saved + shares) / outreach) * 100) : 0;
    p.new_engagement_rate_view = outreach ? round2((views / outreach) * 100) : 0;
    p.interaction_rate_plays = views ? round2(((likes + comments + saved + shares) / views) * 100) : 0;
    p.engagement_rate_reach = reach ? round2(((likes + comments + saved + shares) / reach) * 100) : 0;
  }
  return p;
}

/** Flatten + derive all posts from a raw report payload's posts array. */
export default function normalizePosts(rawPosts) {
  const out = [];
  (Array.isArray(rawPosts) ? rawPosts : []).forEach((post, index) => {
    if (post && post.reportGroup && Array.isArray(post.reports)) {
      post.reports.forEach((gp) => out.push(applyDerived(calculatePostData(gp, index))));
    } else if (post) {
      out.push(applyDerived(calculatePostData(post, index)));
    }
  });
  return out;
}



// WEBPACK FOOTER //
// ./src/components/reports/kobe/reportData.js